import { useState, useRef } from 'react';
import { Form, Row, Col, Button, Image, Card } from 'react-bootstrap';
import ProfileMobileSectionActions from './ProfileMobileSectionActions';
import icImgPreview from './assets/img/ic-img.svg';
import icImgEdit from './assets/img/ic-img-dark.svg';
import icDelete from './assets/img/ic-delete-danger.svg';
import icValidationError from './assets/img/ic-danger.svg';
import { resolveMediaUrl } from './utils/mediaUrl';

const UserAddGeneral = ({ formData, handleChange, handleReset, errors, handleGeneralSubmit, isEditPage, handleDeleteImage, profileImage, companies = [], hideEdit }) => {

    const [isEditing, setIsEditing] = useState(!isEditPage);
    const [tempFormData, setTempFormData] = useState({ ...formData });
    const fileInputRef = useRef(null);
    const [localErrors, setLocalErrors] = useState({});

    const normalizeId = (x) => {
        if (!x) return '';
        if (typeof x === 'string') return x;
        if (typeof x === 'object') {
            if (x._id) return normalizeId(x._id);
            if (x.$oid) return String(x.$oid);
            if (x.id) return normalizeId(x.id);
            if (typeof x.toHexString === 'function') return x.toHexString();
            if (typeof x.toString === 'function' && x.toString !== Object.prototype.toString) {
                const s = x.toString();
                if (/^[a-fA-F0-9]{24}$/.test(s)) return s;
            }
        }
        return '';
    };

    const companyId = normalizeId(formData?.company);
    const companyTitle =
        (Array.isArray(companies) ? companies : [])
            .find(c => normalizeId(c._id ?? c.id ?? c) === companyId)
            ?.title || '';


    const handleFirstNameChange = (e) => {
        const { value } = e.target;

        // Live validation
        if (!value.trim()) {
            setLocalErrors((prev) => ({ ...prev, firstName: 'First name is required.' }));
        } else {
            setLocalErrors((prev) => ({ ...prev, firstName: '' }));
        }

        handleChange({ target: { name: 'firstName', value } });
    };

    const handleFamilyNameChange = (e) => {
        const { value } = e.target;

        // Live validation
        if (!value.trim()) {
            setLocalErrors((prev) => ({ ...prev, familyName: 'Last name is required.' }));
        } else {
            setLocalErrors((prev) => ({ ...prev, familyName: '' }));
        }

        handleChange({ target: { name: 'familyName', value } });
    };

    const handleGenderChange = (e) => {
        const { value } = e.target;

        // Live validation
        if (!value.trim()) {
            setLocalErrors((prev) => ({ ...prev, gender: 'Gender is required.' }));
        } else {
            setLocalErrors((prev) => ({ ...prev, gender: '' }));
        }

        handleChange({ target: { name: 'gender', value } });
    };

    // Enable editing mode
    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // Store previous values
    };

    const handleEditImage = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click(); // ✅ triggers file dialog
        }
    };

    // Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();

        if (typeof handleGeneralSubmit === 'function') {
            const success = await handleGeneralSubmit(e); // ✅ Use return value
            if (success) {
                setIsEditing(false); // ✅ Only if successful
            }
        } else {
            console.error('Error: handleGeneralSubmit is not a function.');
        }
    };

    // Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false); // Exit edit mode

        if (typeof handleReset === 'function') {
            handleReset(tempFormData); // ✅ Restore saved values + clear errors
        }
    };

    return (
        <>
            <Card className="card-premium profile-general-card">
                <Card.Body>
                    {/* Title and Edit Button */}
                    <Row className="align-items-center mb-3">
                        <Col>
                            {isEditPage && !hideEdit && (
                                <div className="float-end d-none d-lg-block">
                                    {!isEditing ? (
                                        <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                    ) : (
                                        <>
                                            <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                            <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                        </>
                                    )}
                                </div>
                            )}
                            <h3 className="text-start">General Info</h3>
                        </Col>
                    </Row>
                    {isEditing ? (
                        <Row>
                            {/* Form Fields */}
                            <Col md={6}>
                                <Row className="mb-3">
                                    <Col md={4} className="d-flex flex-column align-items-center">
                                        <Form.Group controlId="profileImage" className="mb-3 text-center upload-image-wrapper">
                                            {profileImage ? (
                                                <div className="upload-preview editing">
                                                    <Image
                                                        src={resolveMediaUrl(profileImage)}
                                                        alt="Profile Preview"
                                                        width="136"
                                                        height="136"
                                                        roundedCircle
                                                        className="preview-image editing"
                                                    />
                                                    
                                                    <Button variant="light" className="btn-edit" onClick={handleEditImage}>
                                                        <img src={icImgEdit} alt="Edit" />
                                                    </Button>
                                                    <Button variant="light" className="btn-delete" onClick={handleDeleteImage}>
                                                        <img src={icDelete} alt="Delete" />
                                                    </Button>
                                                    <Form.Control
                                                        type="file"
                                                        accept="image/*"
                                                        name="profileImage"
                                                        onChange={handleChange}
                                                        className="upload-input"
                                                        disabled={!isEditing}
                                                        hidden
                                                        ref={fileInputRef}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="upload-box">
                                                    <img src={icImgPreview} alt="Upload Icon" className="upload-icon" />
                                                    <div className="upload-text">Upload logo</div>
                                                    <div className="upload-subtext">JPEG or PNG.</div>
                                                    <Form.Control
                                                        type="file"
                                                        accept="image/*"
                                                        name="profileImage"
                                                        onChange={handleChange}
                                                        className="upload-input"
                                                        disabled={!isEditing}
                                                    />
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={8} className="profile-edit-fields">
                                        <Row className="mb-2">
                                            <Col md={6}>
                                                <Form.Group className="validated" controlId="firstName">
                                                    <Form.Label>First Name <span>*</span></Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="firstName"
                                                        value={formData.firstName}
                                                        onChange={handleFirstNameChange}
                                                        isInvalid={!!errors.firstName || !!localErrors.firstName}
                                                        required
                                                        disabled={!isEditing}
                                                    />
                                                    
                                                    {/* ✅ Custom error icon + tooltip */}
                                                    {(errors.firstName || localErrors.firstName) && (
                                                        <div className="custom-error-icon-wrapper">
                                                            <img
                                                                src={icValidationError}
                                                                alt={errors.firstName || localErrors.firstName}
                                                                className="custom-error-icon"
                                                            />
                                                            <div className="custom-tooltip">
                                                                {errors.firstName || localErrors.firstName}
                                                            </div>
                                                        </div>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="validated" controlId="familyName">
                                                    <Form.Label>Last Name<span>*</span></Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="familyName"
                                                        value={formData.familyName}
                                                        onChange={handleFamilyNameChange}
                                                        isInvalid={!!errors.familyName || localErrors.familyName}
                                                        required
                                                        disabled={!isEditing}
                                                    />
                                                    {/* ✅ Custom error icon + tooltip */}
                                                    {(errors.familyName || localErrors.familyName) && (
                                                        <div className="custom-error-icon-wrapper">
                                                            <img
                                                                src={icValidationError}
                                                                alt={errors.familyName || localErrors.familyName}
                                                                className="custom-error-icon"
                                                            />
                                                            <div className="custom-tooltip">
                                                                {errors.familyName || localErrors.familyName}
                                                            </div>
                                                        </div>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Form.Group className="validated" controlId="gender">
                                                    <Form.Label>Gender <span>*</span></Form.Label>
                                                    <Form.Select
                                                        name="gender"
                                                        value={formData.gender ?? ''}
                                                        onChange={handleGenderChange}
                                                        isInvalid={!!errors.gender || !!localErrors.gender}
                                                        disabled={!isEditing}
                                                    >
                                                        <option value="" disabled>Please choose</option>
                                                        <option value="male">Male</option>
                                                        <option value="female">Female</option>
                                                        <option disabled>--------------------</option>
                                                        <option value="agender">Agender</option>
                                                        <option value="bigender">Bigender</option>
                                                        <option value="cisgender">Cisgender</option>
                                                        <option value="genderfluid">Genderfluid</option>
                                                        <option value="genderqueer">Genderqueer</option>
                                                        <option value="non-binary">Non-binary</option>
                                                        <option value="demiboy">Demiboy</option>
                                                        <option value="demigirl">Demigirl</option>
                                                        <option value="transgender">Transgender</option>
                                                        <option value="two-spirit">Two-Spirit</option>
                                                        <option value="pangender">Pangender</option>
                                                        <option value="neutrois">Neutrois</option>
                                                        <option value="androgynous">Androgynous</option>
                                                        <option value="genderflux">Genderflux</option>
                                                        <option value="polygender">Polygender</option>
                                                        <option value="intergender">Intergender</option>
                                                    </Form.Select>
                                                    {/* ✅ Custom error icon + tooltip */}
                                                    {(errors.gender || localErrors.gender) && (
                                                        <div className="custom-error-icon-wrapper">
                                                            <img
                                                                src={icValidationError}
                                                                alt={errors.gender || localErrors.gender}
                                                                className="custom-error-icon"
                                                            />
                                                            <div className="custom-tooltip">
                                                                {errors.gender || localErrors.gender}
                                                            </div>
                                                        </div>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    ) : (
                        <>
                            <Row className="mb-2">
                                <Col md={12} className="d-flex">
                                    <div className="upload-image-wrapper">
                                        <Form.Group controlId="profileImage" className="mb-3 text-center upload-image-wrapper">
                                            <div className="upload-preview not-editing">
                                                <Image
                                                    src={profileImage?.trim() ? resolveMediaUrl(profileImage) : icImgPreview} // ✅ fallback if profile img is empty
                                                    alt="Profile Preview"
                                                    roundedCircle
                                                    className="preview-image"
                                                    width="136"
                                                    height="136"
                                                />
                                            </div>
                                        </Form.Group>
                                    </div>
                                    <div className="ms-3">
                                        <h1>{formData.firstName} {formData.familyName}</h1>
                                        <p className="mb-2">
                                            {formData.gender}
                                            {companyTitle || formData.company ? (
                                                <>
                                                    <span className="dot"></span>
                                                    {companyTitle || formData.company}
                                                    <span className="dot"></span>
                                                    {formData.companyRole}
                                                </>
                                            ) : (
                                                ''
                                            )}
                                        </p>
                                        {isEditPage && (
                                            <>
                                                <div className="status-wrapper">
                                                    <p>
                                                        <span 
                                                            className={`badge ${formData.activityStatus ? 'bg-active' : 'bg-inactive'}`}
                                                        >
                                                            {formData.activityStatus ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </p>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                    <ProfileMobileSectionActions
                        isEditing={isEditing}
                        onEdit={handleEditClick}
                        onCancel={handleCancelClick}
                        onSave={handleSaveClick}
                        show={isEditPage && !hideEdit}
                    />
                </Card.Body>
            </Card>    
        </>
    );
};

export default UserAddGeneral;
